import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase/firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "./BusinessAccounts.css";
import { useLanguage } from "../../contexts/LanguageContext"; // ⬅️ add this

/* ---- Responsive column count (matches CSS breakpoints) ---- */
const useColumnCount = () => {
  const compute = () => {
    const w = window.innerWidth;
    if (w >= 1800) return 8;
    if (w >= 1500) return 7;
    if (w >= 1300) return 6;
    if (w >= 1200) return 4;
    if (w >= 580) return 3;
    return 2;
  };
  const [count, setCount] = useState(typeof window !== "undefined" ? compute() : 6);

  useEffect(() => {
    const onResize = () => setCount(compute());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return count;
};

const BusinessAccounts = () => {
  const [businessAccounts, setBusinessAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const colCount = useColumnCount();

  // ⬇️ translations
  const { translations: tRaw } = useLanguage();
  const t = {
    businessAccounts: {},
    ...tRaw,
    businessAccounts: { ...tRaw?.businessAccounts },
  };

  // Fetch business accounts (users with isOrganization = true)
  useEffect(() => {
    const fetchBusinessAccounts = async () => {
      try {
        const q = query(collection(db, "users"), where("isOrganization", "==", true));
        const querySnapshot = await getDocs(q);

        const accounts = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setBusinessAccounts(accounts);
      } catch (error) {
        console.error("Error fetching business accounts:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBusinessAccounts();
  }, []);

  const handleOpenProfile = (accountId) => {
    navigate(`/profile/${accountId}`);
  };

  const handleViewStore = (accountId) => {
    navigate(`/${accountId}/store`);
  };

  // Split accounts into columns
  const columns = useMemo(() => {
    const cols = Array.from({ length: colCount }, () => []);
    businessAccounts.forEach((acc, idx) => {
      cols[idx % colCount].push(acc);
    });
    return cols;
  }, [businessAccounts, colCount]);

  if (isLoading) {
    return (
      <div className="business-accounts-container loading">
        <div className="business-accounts-loader" />
      </div>
    );
  }

  if (!businessAccounts.length) {
    return (
      <div className="business-accounts-container empty">
        <p>{t.businessAccounts?.empty || "No business accounts available"}</p>
      </div>
    );
  }

  return (
    <div className="business-accounts-container">
      <div className="business-accounts-viewport">
        <div
          className="business-accounts-grid"
          style={{ gridTemplateColumns: `repeat(${colCount}, 1fr)` }}
        >
          {columns.map((colItems, colIndex) => {
            const durations = [30, 12, 16, 14, 18, 10];
            const duration = durations[colIndex % durations.length] || 16;

            return (
              <div
                key={`col-${colIndex}`}
                className="ba-column"
                style={{ ["--col-duration"]: `${duration}s` }}
              >
                <div className="ba-track">
                  {[...colItems, ...colItems].map((account, i) => (
                    <div
                      key={`${account.id}-${i}`}
                      className="business-account-card"
                      onClick={() => handleViewStore(account.id)}
                    >
                      <div className="business-account-main-image">
                        <img
                          src={account.bannerImage || account.profileImage || "/default-banner.jpg"}
                          alt={`${account.organizationName || account.name || account.username || "Business"} banner`}
                          className="main-image"
                          loading="lazy"
                          draggable="false"
                        />
                      </div>

                      <div className="business-account-profile">
                        <div className="profile-image-container">
                          <img
                            src={account.profileImage || "/default-avatar.png"}
                            alt={`${account.organizationName || account.name || account.username || "Business"} profile`}
                            className="profile-image"
                            loading="lazy"
                            draggable="false"
                          />
                        </div>
                        <div className="profile-info">
                          <h3 className="business-name">
                            {account.organizationName ||
                              account.name ||
                              account.username ||
                              "Business Account"}
                          </h3>
                        </div>
                      </div>

                      <div className="business-account-actions">
                        <button
                          className="business-action-btn open-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenProfile(account.id);
                          }}
                          type="button"
                        >
                          {t.businessAccounts?.open || "OPEN"}
                        </button>
                        <button
                          className="business-action-btn view-store-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewStore(account.id);
                          }}
                          type="button"
                        >
                          {t.businessAccounts?.viewStore || "View Store"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BusinessAccounts;
